import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {Lead} from '../../../../data/domain/lead';
import {signOut} from '@firebase/auth';

import {createE164PhoneNumber} from '../../../../common/strings';
import {leadDTOToExistingSubscriptionDTO, leadDTOToWaitingSubscriptionDTO} from '../../../helper/subscription.helper';
import {FIREBASE_AUTH} from '../../../config/module.di.tokens';
import {SubscriptionService} from '../../../../services/subscription.service';
import {SubscriptionResources} from '../../../../resources/subscription.resources';
import {LeadResources} from '../../../../resources/lead.resources';
import {SubscriptionConfirmationContext} from './subscription.confirmation.context';
import {ErrorReportingApi} from '../../../../common/api/error-reporting.api';
import {SocialTrackService} from '../../../../services/social-track.service';
import {leadToGA} from '../../../../common/ga';
import {addDefaultAnimationIfNeeded} from '../../../helper/lead.helper';
import {AbstractSubscriptionContextService} from '../../../../services/abstract-subscription-context.service';
import {Router} from '@angular/router';
import {SessionDTO, SubscriptionDTO, SubscriptionPaymentDTO} from '@minutespourtoi/api-models/dist/models';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionConfirmationActions {

  auth = inject(FIREBASE_AUTH);
  subscriptionService = inject(SubscriptionService)
  subscriptionContextService : AbstractSubscriptionContextService
  subscriptionResources = inject(SubscriptionResources)
  leadResources = inject(LeadResources)
  errorReportingApi = inject(ErrorReportingApi);
  socialTrackService = inject(SocialTrackService)
  platformId = inject(PLATFORM_ID)
  router = inject(Router)
  private context: SubscriptionConfirmationContext
  protected referralCode: string
  protected session: SessionDTO

  setUp(context: SubscriptionConfirmationContext, session: SessionDTO, referralCode: string) {
    this.context = context;
    this.session = session;
    this.referralCode = referralCode
  }

  failWithMessage(message: string, sourceError: any, showNextSession = false) {
    if (this.context) {
      this.context.paymentContext.set({loaded: false, orderCreated: false, loading: false, error: message, captured: false, authenticationInProgress: false, showNextSession: showNextSession})
      if (sourceError) {
        this.errorReportingApi.report({
          lead: this.leadResources.currentLead.value(),
          email: this.context.confirmationForm?.controls.email.value,
          firstname: this.context.confirmationForm?.controls.firstname.value,
          phone: this.context.confirmationForm?.controls.phone.value,
          message: message ?? sourceError?.message,
          sourceError: sourceError
        })
      }
    }
  }

  failWithTechnicalError(error: Error) {
    this.failWithMessage("Erreur de paiement, cela peut-être dû à un blocage de ta banque, veuillez ressayer ou nous contacter sur contact@7minutespourtoi.fr.", error)
  }

  async sendSubscription(leadDTO: Lead, paymentMode: string) {

    let subscriptionDTO = await this.leadToSubscription(leadDTO, paymentMode)
    if (this.auth.currentUser && subscriptionDTO.phone != this.auth.currentUser.phoneNumber) {
      await signOut(this.auth)
    }
    subscriptionDTO = await this.subscriptionService.createOrUpdateSubscription(subscriptionDTO)

    this.applySubscriptionOnLead(subscriptionDTO)
    this.trackAddToCart(leadDTO)

    return subscriptionDTO;
  }

  async sendSubscriptionAndThanks(leadDTO: Lead, paymentMode: string) {
    try {
      await this.sendSubscription(leadDTO, paymentMode)
      await this.router.navigate(['/subscribe-v2', this.session.id, 'thanks'], {
        queryParams: {
          referralCode : this.referralCode
        }
      })

    } catch (error) {
      this.failWithMessage(error.message, error)
    }
  }

  async leadToSubscription(leadDTO: Lead, paymentMode: any) {

    const subscription = new SubscriptionDTO()
    if (paymentMode) {
      subscription.payment = new SubscriptionPaymentDTO()
      subscription.payment.payMode = paymentMode
      subscription.payment.amount = this.subscriptionResources.calculatePrice.value()?.totalPrice
    } else {
      subscription.payment = leadDTO?.subscription?.payment
    }

    if (this.context?.confirmationForm) {
      subscription.phone = createE164PhoneNumber(this.context?.confirmationForm.controls.phone.value)
      subscription.email = this.context?.confirmationForm.controls.email.value
      subscription.firstname = this.context?.confirmationForm.controls.firstname.value

      if (!subscription.firstname) {
        throw new Error("Merci de ressaisir ton prénom.")

      } else if (!subscription.phone) {
        throw new Error("Merci de ressaisir ton numéro de télephone.")

      } else if (!subscription.email) {
        throw new Error("Merci de ressaisir ton Email.")
      }
    } else {
      subscription.phone = leadDTO.phone
      subscription.email = leadDTO.email
      subscription.firstname = leadDTO.firstname
    }
    await addDefaultAnimationIfNeeded(leadDTO, this.session)

    return leadDTOToExistingSubscriptionDTO(this.session, leadDTO, subscription)
  }

  async waitSubscriptionOnCompleteAge() {
    await this.sendWaitSubscription()
    this.failWithMessage("Les inscriptions sont complètes sur ta tranche d'âge. Ton inscription a été enregistrée en file d'attente, nous te contacterons dès que possible si des places sont à nouveau disponibles.",
      null, true)
  }
  async waitSubscriptionOnForbiddenAge() {
    await this.sendWaitSubscription()
    this.failWithMessage("Nous avons beaucoup de demandes sur ta tranche d'âge et les inscriptions sont pour l'instant mises en veille. Ton inscription a été enregistrée en file d'attente, nous te contacterons dès que possible si des places sont à nouveau disponibles.",
      null, true)
  }
  async waitSubscriptionOnUnauthorizedOldClient() {
    await this.sendWaitSubscription()
    this.failWithMessage("Tu as déja participé à un évenement chez nous par le passé et nous avons déja eu beaucoup d'inscrits d'anciens clients. Afin de te garantir de ne pas rencontrer des célibataires que tu as déja vu, nous sommes au regret de ne pas pouvoir accepter ton inscription." +
      " Celle-ci a été enregistrée en file d'attente, nous te contacterons dès que possible si la situation évolue.",
      null, true)
  }

  private async sendWaitSubscription() {
    const leadDTO = this.leadResources.currentLead.value();
    this.applyFormOnLead(leadDTO)

    await addDefaultAnimationIfNeeded(leadDTO, this.session)

    this.subscriptionResources.waitSubscriptionRequest.set(leadDTOToWaitingSubscriptionDTO(this.session, leadDTO))
    this.trackAddToCart(leadDTO)
  }

  applySubscriptionOnLead(subscriptionDTO: SubscriptionDTO) {
    const leadDTO = this.leadResources.currentLead.value();
    leadDTO.subscription = subscriptionDTO
    this.applyFormOnLead(leadDTO)

    this.leadResources.saveLeadRequest.set(leadDTO)

    return leadDTO
  }

  private applyFormOnLead(leadDTO: Lead) {
    if (this.context?.confirmationForm) {
      leadDTO.phone = createE164PhoneNumber(this.context?.confirmationForm.controls.phone.value)
      leadDTO.email = this.context?.confirmationForm.controls.email.value
      leadDTO.firstname = this.context?.confirmationForm.controls.firstname.value
    }
  }

  private trackAddToCart(leadDTO: Lead) {
    this.socialTrackService.trackAddToCart({
      fbData: [leadDTO.sessionId],
      eventId: leadDTO.sessionId,
      fbValue: leadDTO?.offer?.price ?? 0,
      fbDataDetails: [
        {
          id: leadDTO.sessionId,
          quantity: 1,
          item_price: leadDTO?.offer?.price ?? 0
        }
      ],
      gaData: leadToGA(leadDTO),
      tikTokValue: null,
      tikTokProductId: null
    })
  }
}
